import { create } from 'zustand';

import type { AuthStore, AuthUser, GoogleAuthKitConfig, UseAuthStore } from './types';

export function createAuthStore(config: GoogleAuthKitConfig): UseAuthStore {
  const { api, logPrefix = '[auth]' } = config;

  const useAuthStore: UseAuthStore = create<AuthStore>((set, get) => ({
    isInitialized: false,
    isAuthenticated: false,
    isRestoringSession: false,
    isLoggingOut: false,
    user: undefined,
    authError: null,
    accessTokenExp: null,

    initialize: () => {
      set({ isInitialized: true });
    },

    handleSignin: (user: AuthUser, accessTokenExp: number | null = null) => {
      set({ isAuthenticated: true, user, authError: null, accessTokenExp });
    },

    handleSignout: () => {
      set({ isAuthenticated: false, user: undefined, authError: null, accessTokenExp: null });
    },

    setAuthError: error => {
      set({ authError: error });
    },

    restoreSession: async () => {
      set({ isRestoringSession: true });
      try {
        const session = await api.getSession();
        if (session.valid && session.user) {
          set({
            isAuthenticated: true,
            user: session.user,
            authError: null,
            accessTokenExp: session.accessTokenExp ?? null,
          });
          return true;
        }
        return false;
      } catch (error) {
        console.error(`${logPrefix} Failed to restore session:`, error);
        set({ authError: 'Failed to verify session. Please try signing in again.' });
        return false;
      } finally {
        set({ isRestoringSession: false });
      }
    },

    logout: async () => {
      set({ isLoggingOut: true });
      try {
        await api.logout();
      } catch (error) {
        console.error(`${logPrefix} Logout API call failed:`, error);
      } finally {
        set({ isLoggingOut: false });
      }
      get().handleSignout();
    },
  }));

  return useAuthStore;
}
